import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaHeart } from 'react-icons/fa';
import { IoSparkles } from 'react-icons/io5';

import { client } from '../client';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Input, Label, RadioGroup, Select, Textarea } from '../components/ui';
import '../components/ui/ui.css';
import './index.css';

const WishlistApply = () => {
  const { wishlistId } = useParams();
  const decodedWishlistId = decodeURIComponent(wishlistId || '');
  const [wishlist, setWishlist] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(true);
  const [applicantsCount, setApplicantsCount] = useState(0);
  const [values, setValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!decodedWishlistId) {
      setWishlistLoading(false);
      return;
    }
    client
      .fetch('*[_type == "datingWishlist" && _id == $wishlistId][0]', { wishlistId: decodedWishlistId })
      .then((data) => {
        setWishlist(data || null);
      })
      .finally(() => {
        setWishlistLoading(false);
      });
  }, [decodedWishlistId]);

  useEffect(() => {
    if (!decodedWishlistId) {
      return;
    }
    client
      .fetch('count(*[_type == "datingWishlistApplication" && wishlist._ref == $wishlistId])', {
        wishlistId: decodedWishlistId,
      })
      .then((count) => {
        setApplicantsCount(count || 0);
      });
  }, [decodedWishlistId, submitted]);

  const initialValues = useMemo(() => {
    if (!wishlist?.fields?.length) {
      return {};
    }
    return wishlist.fields.reduce((acc, field) => {
      acc[field.key] = field.type === 'multi' ? [] : '';
      return acc;
    }, {});
  }, [wishlist?.fields]);

  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  const updateFieldValue = (field, value) => {
    setValues((prev) => ({
      ...prev,
      [field.key]: value,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    (wishlist?.fields || []).forEach((field) => {
      const value = values[field.key];
      if (!field.required) {
        return;
      }
      if (field.type === 'multi') {
        if (!value || value.length === 0) {
          nextErrors[field.key] = 'This field is required.';
        }
        return;
      }
      if (value === undefined || value === null || String(value).trim() === '') {
        nextErrors[field.key] = 'This field is required.';
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!wishlist) {
      return;
    }
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      await client.create({
        _type: 'datingWishlistApplication',
        wishlist: { _type: 'reference', _ref: wishlist._id },
        answers: values,
      });
      setSubmitted(true);
    } catch (error) {
      console.error('Failed to submit wishlist application:', error);
    } finally {
      setLoading(false);
    }
  };

  if (wishlistLoading) {
    return (
      <div className="wishlist-apply-shell">
        <div className="ui-container wishlist-apply-content">
          <Card className="wishlist-loading-card">
            <CardContent>
              <p className="ui-muted">Getting things ready for you…</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!wishlist) {
    return (
      <div className="wishlist-apply-shell">
        <div className="ui-container">
          <Card>
            <CardContent>
              <p className="ui-muted">Waitlist not found. Ask the owner to open Dashboard and save/publish it.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="wishlist-apply-shell">
        <div className="ui-container wishlist-apply-content">
          <Card className="wishlist-submit-card">
            <CardHeader>
              <CardTitle>
                <span className="card-title-with-icon">
                  <FaHeart />
                  You&apos;re on the list
                </span>
              </CardTitle>
              <CardDescription>Your application is in. Now the fun part — waiting to see if the spark is mutual.</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="submit-rank">
                You&apos;re <strong>#{applicantsCount}</strong> in line
              </p>
              <p className="submit-flirt-line">Make it count. They&apos;ll be reading every word.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="wishlist-apply-shell">
      <div className="ui-container wishlist-apply-content">
        <div className="waitlist-hero">
          <p className="hero-badge">
            <IoSparkles />
            Your move
          </p>
          <h1>{wishlist.title}</h1>
          <p>{wishlist.description}</p>
          <div className="hero-stats">
            <p className="hero-stat">
              <strong>{applicantsCount}</strong>
              already tried their luck
            </p>
            <p className="hero-stat">
              <strong>Limited</strong>
              spots — stand out
            </p>
          </div>
        </div>

        <Card className="wishlist-main-card">
          <CardHeader>
            <CardTitle>Tell them why you&apos;re worth the wait</CardTitle>
            <CardDescription>Be charming, be honest, be unforgettable. This is your opening line.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="wishlist-form-meta">
              <p className="wishlist-apply-count">
                {applicantsCount === 0
                  ? 'Be the first to shoot your shot'
                  : <>Join <strong>{applicantsCount}</strong> others who already applied</>}
              </p>
            </div>
            <div className="ui-grid">
              {(wishlist.fields || []).map((field) => (
                <div key={field.key} className="wishlist-field-block">
                  <Label htmlFor={field.key}>
                    {field.label}
                    {field.required ? ' *' : ''}
                  </Label>

                  {field.type === 'text' || field.type === 'number' ? (
                    <Input
                      id={field.key}
                      type={field.type === 'number' ? 'number' : 'text'}
                      value={values[field.key] || ''}
                      onChange={(event) => updateFieldValue(field, event.target.value)}
                    />
                  ) : null}

                  {field.type === 'textarea' ? (
                    <Textarea
                      id={field.key}
                      value={values[field.key] || ''}
                      onChange={(event) => updateFieldValue(field, event.target.value)}
                    />
                  ) : null}

                  {field.type === 'select' ? (
                    <Select
                      id={field.key}
                      value={values[field.key] || ''}
                      onChange={(event) => updateFieldValue(field, event.target.value)}
                    >
                      <option value="">Select an option</option>
                      {(field.options || []).map((option) => (
                        <option key={`${field.key}-${option}`} value={option}>
                          {option}
                        </option>
                      ))}
                    </Select>
                  ) : null}

                  {field.type === 'radio' ? (
                    <RadioGroup
                      name={field.key}
                      options={(field.options || []).map((option) => ({ label: option, value: option }))}
                      value={values[field.key] || ''}
                      onChange={(event) => updateFieldValue(field, event.target.value)}
                    />
                  ) : null}

                  {field.type === 'multi' ? (
                    <div className="multi-options-grid">
                      {(field.options || []).map((option) => {
                        const checked = (values[field.key] || []).includes(option);
                        return (
                          <label
                            key={`${field.key}-${option}`}
                            className={`ui-checkbox-wrap multi-option-chip ${checked ? 'checked' : ''}`}
                          >
                            <input
                              type="checkbox"
                              className="ui-checkbox"
                              checked={checked}
                              onChange={(event) => {
                                const prevValues = values[field.key] || [];
                                if (event.target.checked) {
                                  updateFieldValue(field, [...prevValues, option]);
                                } else {
                                  updateFieldValue(
                                    field,
                                    prevValues.filter((item) => item !== option)
                                  );
                                }
                              }}
                            />
                            <span>{option}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}

                  {errors[field.key] ? <p className="wishlist-error">{errors[field.key]}</p> : null}
                </div>
              ))}
            </div>

            <div className="wishlist-submit-row">
              <Button onClick={handleSubmit} disabled={loading} className="wishlist-submit-btn">
                {loading ? 'Sending your charm…' : 'Shoot your shot'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WishlistApply;